import React from 'react';
import Plot from 'react-plotly.js';
import { PoolDataHookResult } from '../../types/pool';
import { POOL_CONFIGS, DEX_INFO } from '../../config/pools';
import { getBarChartLayout, getBaseConfig } from './chartConfig';
import './ChartStyles.css';

interface TVLByDEXChartProps {
  poolsData: { [key: string]: PoolDataHookResult };
  className?: string;
}

export const TVLByDEXChart: React.FC<TVLByDEXChartProps> = ({ poolsData, className }) => {
  // Prepare data for the DEX chart
  const chartData = React.useMemo(() => {
    const dexTotals: { [dex: string]: number } = {};
    
    POOL_CONFIGS.forEach((poolConfig) => {
      const poolKey = `${poolConfig.dex}_${poolConfig.address}`;
      const poolData = poolsData[poolKey];
      
      if (poolData?.snapshots && poolData.snapshots.length > 0) {
        const latestSnapshot = poolData.snapshots[poolData.snapshots.length - 1];
        const tvl = latestSnapshot.total_value_locked_usd || 0;
        
        if (tvl > 0) {
          dexTotals[poolConfig.dex] = (dexTotals[poolConfig.dex] || 0) + tvl;
        }
      }
    });
    
    const labels = Object.keys(dexTotals).map(dex => DEX_INFO[dex as keyof typeof DEX_INFO]?.name || dex);
    const values = Object.values(dexTotals);
    const colors = Object.keys(dexTotals).map(dex => DEX_INFO[dex as keyof typeof DEX_INFO]?.color || '#666666');
    
    return {
      labels,
      values,
      colors
    };
  }, [poolsData]);

  const data = [{
    x: chartData.labels,
    y: chartData.values,
    type: 'bar' as const,
    name: 'DEX', // Proper name for the data series
    marker: {
      color: chartData.colors,
      line: {
        color: '#404040',
        width: 1
      }
    },
    text: chartData.values.map(val => `$${(val / 1000000).toFixed(1)}M`),
    textposition: 'outside' as const,
    textfont: {
      color: '#ffffff',
      size: 11
    },
    hovertemplate: '<b>%{x}</b><br>' +
                   'TVL: $%{y:,.0f}<br>' +
                   '<extra></extra>',
    showlegend: false, // Hide the legend since we don't need it for a single bar series
    width: 0.7 // Make bars wider (default is 0.8, lower value = wider bars)
  }];

  const layout = {
    ...getBarChartLayout('DEX Liquidity Distribution', 'Decentralized Exchange', 'Total Value Locked (USD)'),
    height: 450 // Increased to match pie chart height
  };

  return (
    <div className={`chart-container ${className || ''}`}>
      <h2 className="chart-title ev-accent separator-wide">DEX Liquidity Distribution</h2>
      <Plot
        data={data}
        layout={layout}
        style={{ width: '100%', height: '450px' }}
        config={getBaseConfig()}
      />
    </div>
  );
};
